import * as z from "zod";
import { Link, createFileRoute } from "@tanstack/react-router";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { ArrowLeftIcon } from "lucide-react";
import { api } from "@/lib/http";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

const userSchema = z.object({
  id: z.string(),
  full_name: z.string(),
  email: z.string(),
  is_active: z.boolean(),
  created_at: z.string(),
  updated_at: z.string().nullable(),
});

type User = z.infer<typeof userSchema>;

function StatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
        isActive
          ? "border-[#ABEFC6] bg-[#ECFDF3] text-[#067647] dark:border-emerald-400/30 dark:bg-emerald-500/15 dark:text-emerald-300"
          : "border-[#E9EAEB] dark:border-border bg-[#FAFAFA] dark:bg-muted/40 text-[#414651] dark:text-foreground",
      )}
    >
      {isActive ? "Active" : "Inactive"}
    </span>
  );
}

function userDetailQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["users", "detail", id],
    async queryFn() {
      const user = await api.auth.raw.get(`api/v1/users/${id}`).json<User>();
      return user;
    },
  });
}

export const Route = createFileRoute("/_app/users/$id")({
  component: RouteComponent,
  loader({ context: { queryClient }, params }) {
    queryClient.ensureQueryData(userDetailQueryOptions(params.id));
  },
});

function RouteComponent() {
  const { id } = Route.useParams();
  const { data: user, isLoading } = useQuery(userDetailQueryOptions(id));

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <Skeleton className="h-8 w-32" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-6 w-56" />
            <Skeleton className="h-6 w-32" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <Link
            to="/users"
            className={buttonVariants({ variant: "outline", size: "sm" })}
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Teams
          </Link>
        </div>
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">User not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/users">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
        </Button>
        <h1 className="text-3xl font-bold">Team Member Details</h1>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Name
                </label>
                <p className="mt-1 text-lg font-semibold break-all">
                  {user.full_name}
                </p>
              </div>
              <div className="min-w-0">
                <label className="text-sm font-medium text-muted-foreground">
                  Email
                </label>
                <p className="mt-1 text-lg font-semibold break-all">
                  {user.email}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Active Status
                </label>
                <div className="mt-1">
                  <StatusBadge isActive={user.is_active} />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Created At
                </label>
                <p className="mt-1">
                  {new Date(user.created_at).toLocaleString()}
                </p>
              </div>
              {user.updated_at && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Updated At
                  </label>
                  <p className="mt-1">
                    {new Date(user.updated_at).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
